import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";

export default function VerifyEmailChange() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setErrorMessage("No verification token provided in the URL.");
      return;
    }

    const verifyToken = async () => {
      try {
        await api.post("/auth/verify-email-change", { token });
        setStatus("success");
      } catch (err) {
        setStatus("error");
        setErrorMessage(
          err.response?.data?.detail || "Invalid or expired verification link. It may have already been used."
        );
      }
    };

    verifyToken();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-zinc-50">
      <div className="w-full max-w-sm space-y-6 bg-white p-8 rounded-xl shadow-sm border border-border text-center">
        {status === "verifying" && (
          <div className="space-y-4">
            <h2 className="font-heading font-black text-2xl tracking-tighter">Verifying Email</h2>
            <p className="text-sm text-zinc-500">Please wait while we verify your new email address...</p>
            <div className="flex justify-center py-4">
              <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
            </div>
          </div>
        )}

        {status === "success" && (
          <div className="space-y-6">
            <h2 className="font-heading font-black text-2xl tracking-tighter">Email Verified</h2>
            <p className="text-sm text-zinc-500">
              Your email address has been successfully verified and updated.
            </p>
            <Button asChild className="w-full bg-zinc-900 text-white hover:bg-zinc-800">
              <Link to="/login">Return to Login</Link>
            </Button>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-6">
            <h2 className="font-heading font-black text-2xl tracking-tighter text-red-600">Verification Failed</h2>
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
              {errorMessage}
            </div>
            <Button asChild className="w-full bg-zinc-900 text-white hover:bg-zinc-800">
              <Link to="/settings/account">Go to Account Settings</Link>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
